import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { api, tokens } from '../lib/api';
import { useAuth } from '../lib/auth';
import DonemSecici, { donemYazi, useDonem, type Donem } from './DonemSecici';
import Ipucu from './Ipucu';

/**
 * RAPOR İSKELETİ — bütün rapor sayfalarının ortak parçaları: dönemle veri
 * çekme, başlık (Yenile · Yazdır/PDF · Excel), raporlar arası gezinme, tablo.
 */
export const RAPORLAR = [
  { to: '/rapor/satis', ad: 'Satış' },
  { to: '/rapor/musteriler', ad: 'Müşteriler' },
  { to: '/rapor/urunler', ad: 'Ürünler' },
  { to: '/rapor/finans', ad: 'Finans' },
  { to: '/rapor/tahsilat-riski', ad: 'Tahsilat riski' },
  { to: '/rapor/siparisler', ad: 'Siparişler' },
  { to: '/rapor/temsilciler', ad: 'Temsilciler' },
  { to: '/rapor/stok', ad: 'Stok ve maliyet' },
] as const;

/** Dönem değişince yeniden çeker; eski yanıt yenisinin üstüne yazmaz. */
export function useRapor<T>(uc: string, acik = true) {
  const { donem, ayarla, sorgu } = useDonem();
  const [veri, setVeri] = useState<T | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const sira = useRef(0);
  const yukle = useCallback(() => {
    if (!acik) return;
    const benim = ++sira.current;
    setYukleniyor(true); setHata(null);
    api<T>(`/api/rapor/${uc}?${sorgu}`)
      .then((r) => { if (benim === sira.current) setVeri(r); })
      .catch((e) => { if (benim === sira.current) setHata((e as Error).message); })
      .finally(() => { if (benim === sira.current) setYukleniyor(false); });
  }, [uc, sorgu, acik]);
  useEffect(yukle, [yukle]);
  return { veri, hata, yukleniyor, yenile: yukle, donem, ayarla, sorgu };
}

/** CSV'yi oturum jetonuyla indirir (bağlantıya jeton yazılmaz). */
export async function csvIndir(uc: string, sorgu: string): Promise<void> {
  const cagir = () => fetch(`/api/rapor/${uc}?${sorgu}&bicim=csv`, { headers: tokens.access ? { authorization: `Bearer ${tokens.access}` } : {} });
  let r = await cagir();
  if (r.status === 401) { await api('/api/me').catch(() => undefined); r = await cagir(); }
  if (!r.ok) throw new Error('Dosya hazırlanamadı');
  const ad = /filename="([^"]+)"/.exec(r.headers.get('content-disposition') ?? '')?.[1] ?? `${uc}.csv`;
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement('a');
  a.href = url; a.download = ad; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function RaporGezgin({ sorgu }: { sorgu: string }) {
  const { can } = useAuth();
  return (
    <nav className="ccd-rapor-gezgin" aria-label="Raporlar">
      <NavLink to={`/?${sorgu}`} end className={({ isActive }) => `ccd-rapor-hap${isActive ? ' ccd-is-active' : ''}`}>Genel özet</NavLink>
      {RAPORLAR.filter((r) => r.to !== '/rapor/stok' || can('quote.cost')).map((r) => (
        <NavLink key={r.to} to={`${r.to}?${sorgu}`} className={({ isActive }) => `ccd-rapor-hap${isActive ? ' ccd-is-active' : ''}`}>{r.ad}</NavLink>
      ))}
    </nav>
  );
}

type SayfaProps = {
  baslik: string; aciklama?: string;
  r: { hata: string | null; yukleniyor: boolean; yenile: () => void; donem: Donem; ayarla: (d: Donem) => void; sorgu: string; veri: unknown };
  /** Excel'e aktarılacak uç ve düğme ipucu. */
  csv?: { uc: string; ad: string };
  /** Dönem çubuğunun altındaki not (ör. "yaşlandırma bugüne göredir"). */
  donemNotu?: string;
  children: ReactNode;
};

export function RaporSayfa({ baslik, aciklama, r, csv, donemNotu, children }: SayfaProps) {
  const [csvHata, setCsvHata] = useState<string | null>(null);
  const [iniyor, setIniyor] = useState(false);
  async function indir() {
    if (!csv) return;
    setCsvHata(null); setIniyor(true);
    try { await csvIndir(csv.uc, r.sorgu); } catch (e) { setCsvHata((e as Error).message); } finally { setIniyor(false); }
  }
  return (
    <>
      <RaporGezgin sorgu={r.sorgu} />
      <div className="ccd-rapor-baslik">
        <div style={{ minWidth: 0 }}>
          <div className="ccd-eyebrow">Raporlar · {donemYazi(r.donem)}</div>
          <h1 className="ccd-title">{baslik}</h1>
          {aciklama && <div className="ccd-meta ccd-rapor-aciklama">{aciklama}</div>}
        </div>
        <div className="ccd-row ccd-rapor-dugmeler">
          {csv && <button className="ccd-btn ccd-btn-sm" onClick={() => void indir()} disabled={iniyor} title={`${csv.ad} — Excel'de açılan CSV dosyası`}>{iniyor ? 'Hazırlanıyor…' : "Excel'e aktar"}</button>}
          <button className="ccd-btn ccd-btn-sm" onClick={() => window.print()} title="Tarayıcının yazdırma penceresi açılır; hedef olarak PDF seçebilirsiniz">Yazdır / PDF</button>
          <button className="ccd-btn ccd-btn-sm" onClick={r.yenile} disabled={r.yukleniyor} title="Rakamları yeniden çek">{r.yukleniyor ? 'Yükleniyor…' : 'Yenile'}</button>
        </div>
      </div>
      <DonemSecici donem={r.donem} ayarla={r.ayarla} not={donemNotu} />
      {(r.hata || csvHata) && (
        <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>{r.hata ?? csvHata}</span></div>
      )}
      {r.veri == null && !r.hata ? <div className="ccd-inline-note">Yükleniyor…</div> : r.veri != null && (
        <div className={`ccd-stack${r.yukleniyor ? ' ccd-rapor-bekliyor' : ''}`}>{children}</div>
      )}
    </>
  );
}

/** Başlıklı kart: grafik ya da tablo kutusu. */
export function Kutu({ baslik, not, sag, children, duz, fihrist }: {
  baslik: string; not?: ReactNode; sag?: ReactNode; children: ReactNode; duz?: boolean;
  /** Fihrist maddesinin kimliği: başlığa «?» koyar, kutuya sağ tıklayınca "Bu nedir?" açılır. */
  fihrist?: string;
}) {
  return (
    <section className={`ccd-card ccd-kutu${duz ? ' ccd-card-flush' : ''}`} data-fihrist={fihrist}>
      <div className="ccd-kutu-baslik">
        <div style={{ minWidth: 0 }}>
          <h2 className="ccd-heading">{baslik}{fihrist && <> <Ipucu id={fihrist} /></>}</h2>
          {not && <div className="ccd-meta">{not}</div>}
        </div>
        {sag}
      </div>
      {children}
    </section>
  );
}

export type Kolon<T> = {
  baslik: string; h: (r: T, i: number) => ReactNode; sayi?: boolean; dar?: boolean; ipucu?: string;
  /** Fihrist maddesinin kimliği: başlığa «?» koyar; sütunun hücrelerine sağ tıklayınca "Bu nedir?" açılır. */
  fihrist?: string;
  /**
   * TELEFON (≤640 px) — tablo kart listesine dönüşür (bkz. `ui/tabloKart.ts`). Varsayılan: ilk sütun kart
   * başlığı, 7 ve üzeri sütunlu tabloda ilk 5'ten sonrası karta dokununca açılır. Bunlar önceliği elle verir:
   *  · `mobilBaslik`: kart başlığı bu sütundur (ilk sütun yerine)
   *  · `mobilAlt`: başlığın hemen altında, etiketsiz ikinci satır (kod, tarih gibi)
   *  · `mobilGizle`: yalnız karta dokununca açılan ayrıntıda görünür
   *  · `mobilGoster`: çok sütunlu tabloda da hep görünür (otomatik gizlemeden muaf)
   */
  mobilBaslik?: boolean; mobilAlt?: boolean; mobilGizle?: boolean; mobilGoster?: boolean;
};
const mobilTur = <T,>(k: Kolon<T>): string | undefined =>
  k.mobilBaslik ? 'baslik' : k.mobilAlt ? 'alt' : k.mobilGizle ? 'gizli' : k.mobilGoster ? 'goster' : undefined;
/** Başlığı sabit, kaydırılabilir tablo. Uzun listede "tümünü göster". */
export function Tablo<T>({ kolonlar, satirlar, bos, ilk = 50, satirSinif, alt }: {
  kolonlar: Kolon<T>[]; satirlar: T[]; bos: ReactNode; ilk?: number; satirSinif?: (r: T) => string | undefined; alt?: ReactNode;
}) {
  const [hepsi, setHepsi] = useState(false);
  if (satirlar.length === 0) return <div className="ccd-empty">{bos}</div>;
  const gorunen = hepsi ? satirlar : satirlar.slice(0, ilk);
  return (
    <>
      <div className="ccd-table-wrap ccd-rapor-tablo">
        <table className="ccd-table">
          <thead>
            <tr>{kolonlar.map((k) => <th key={k.baslik} className={`ccd-th${k.sayi ? ' ccd-td-num' : ''}`} title={k.fihrist ? undefined : k.ipucu} data-fihrist={k.fihrist}>
              {k.baslik}{k.fihrist && <> <Ipucu id={k.fihrist} not={k.ipucu} /></>}
            </th>)}</tr>
          </thead>
          <tbody>
            {gorunen.map((r, i) => (
              <tr key={i} className={`ccd-tr ${satirSinif?.(r) ?? ''}`}>
                {kolonlar.map((k) => {
                  const tur = mobilTur(k);
                  return <td key={k.baslik} className={`ccd-td${k.sayi ? ' ccd-td-num ccd-td-nowrap' : ''}${k.dar ? ' ccd-td-nowrap' : ''}`} data-fihrist={k.fihrist}
                             data-kart-sabit={tur ? '' : undefined} data-kart={tur === 'goster' ? undefined : tur}>{k.h(r, i)}</td>;
                })}
              </tr>
            ))}
          </tbody>
          {alt && <tfoot>{alt}</tfoot>}
        </table>
      </div>
      {satirlar.length > ilk && (
        <button className="ccd-btn ccd-btn-ghost ccd-btn-sm ccd-rapor-hepsi" onClick={() => setHepsi((x) => !x)}>
          {hepsi ? `Yalnız ilk ${ilk} satırı göster` : `Tümünü göster (${satirlar.length} satır)`}
        </button>
      )}
    </>
  );
}
